// #753 — Conversations sub-page. Holds the two flat sections that used
// to live near the bottom of /admin/users/{id}:
//   - Companion calls (with voice-engagement health badge)
//   - Conversations (raw transcript list)
// Both tables use the shared datagrid wrapper so long histories scroll
// inside the grid instead of growing the page.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import {
  formatCallHeading,
  formatAttemptLabel,
  formatOutcome,
  formatWhen,
} from '../utils/companionCallHistory';
import {
  userSubPageStyles, fmtDate, adminFetch,
} from './userSubPageStyles';

// v1.2.0 (#873) — the topic-activity dashboard that briefly lived
// above the tables moved to its own /cards sub-page after staging
// review. This page is back to its pre-#873 shape.
const VERSION = 'v1.2.0';

export default function AdminUserConversations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [companionCalls, setCompanionCalls] = useState(null);
  const [voiceEngagement, setVoiceEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, cc, ve] = await Promise.all([
          adminApi.getUser(id),
          adminFetch(`/api/admin/users/${id}/companion-calls`),
          adminFetch(`/api/admin/users/${id}/voice-engagement`),
        ]);
        if (cancelled) return;
        setUser(u);
        setCompanionCalls(cc?.companion_calls || []);
        setVoiceEngagement(ve?.voice_engagement || 'active');
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <AdminLayout title="Conversations">
      <style>{userSubPageStyles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · Conversations sub-page
          </div>
        </div>
      )}

      <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>Companion calls</span>
        {voiceEngagement && voiceEngagement !== 'active' && (
          <span
            style={{
              fontSize: '0.7rem', padding: '2px 10px', borderRadius: 12,
              background: voiceEngagement === 'inactive' ? '#5a1f1f' : '#5a4a1f',
              color: voiceEngagement === 'inactive' ? '#ffb8b8' : '#ffd88a',
              textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
            }}
            title={
              voiceEngagement === 'inactive'
                ? 'No engaged companion calls in the last 5 attempts'
                : 'Mixed engagement in the last 5 attempts'
            }
          >
            voice {voiceEngagement}
          </span>
        )}
        <span className="section-sub">
          User-initiated calls only. Click a row to open the conversation.
        </span>
      </div>
      {companionCalls === null && <p className="loading-state">Loading…</p>}
      {companionCalls && companionCalls.length === 0 && (
        <div className="empty-state">
          No companion calls yet — user-initiated calls only, or this is a fresh account.
        </div>
      )}
      {companionCalls && companionCalls.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Trigger</th>
                <th>Attempt</th>
                <th>Outcome</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {companionCalls.map(call => {
                const outcome = formatOutcome(call, 'admin');
                const attempt = formatAttemptLabel(call);
                return (
                  <tr
                    key={call.conversation_id || call.pending_call_id || call.started_at}
                    className={call.conversation_id ? 'clickable' : ''}
                    onClick={() => {
                      if (call.conversation_id) {
                        navigate(`/admin/conversations/${call.conversation_id}`);
                      }
                    }}
                  >
                    <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {formatWhen(call.started_at)}
                    </td>
                    <td style={{ color: '#E8845A', fontWeight: 600 }}>
                      {formatCallHeading(call)}
                    </td>
                    <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {attempt || '1 of 1'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ marginRight: 6 }}>{outcome.icon}</span>
                      <span style={{ fontSize: '0.85rem' }}>{outcome.label}</span>
                    </td>
                    <td style={{ color: '#888', fontSize: '0.82rem' }}>
                      {call.is_sensitive
                        ? <em>(personal — see transcript)</em>
                        : (call.summary || outcome.copy)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="section-title">
        Conversations
        <span className="section-sub">Raw transcript list. Click a row to open.</span>
      </div>
      {user && user.conversations && user.conversations.length > 0 ? (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Turns</th>
                <th>Sensitive</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {user.conversations.map(c => (
                <tr
                  key={c.id}
                  className="clickable"
                  onClick={() => navigate(`/admin/conversations/${c.id}`)}
                >
                  <td>{fmtDate(c.started_at)}</td>
                  <td>{c.turn_count}</td>
                  <td>
                    {c.is_sensitive
                      ? <span className="badge badge-sensitive">Sensitive</span>
                      : <span className="badge badge-ok">Normal</span>
                    }
                  </td>
                  <td style={{ color: '#666', fontSize: '0.85rem' }}>
                    {c.summary_for_family
                      ? c.summary_for_family.slice(0, 80) + (c.summary_for_family.length > 80 ? '…' : '')
                      : '—'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : user ? (
        <div className="empty-state">No conversations yet.</div>
      ) : null}
    </AdminLayout>
  );
}
